import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  FileText, Layers, ClipboardList, Mic, BarChart3,
  X, ChevronLeft, ChevronRight, CheckCircle2
} from 'lucide-react';

const TYPES = {
  report_content: { icon: FileText, label: 'Rapor', color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20' },
  flashcards: { icon: Layers, label: 'Bilgi Kartlari', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
  extra_quiz: { icon: ClipboardList, label: 'Quiz', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
  audio_url: { icon: Mic, label: 'Podcast', color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20' },
  slides_url: { icon: BarChart3, label: 'Slayt', color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/20' },
};

export default function MaterialsBar({ materials, initialType }) {
  const [activeType, setActiveType] = useState(initialType || null);
  const [flashcardIdx, setFlashcardIdx] = useState(0);
  const [flashcardFlipped, setFlashcardFlipped] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});

  if (!materials) return null;

  const available = Object.entries(TYPES).filter(([key]) => {
    const val = materials[key];
    if (!val) return false;
    if (typeof val === 'string') return val.length > 10;
    if (Array.isArray(val)) return val.length > 0;
    return !!val;
  });

  if (available.length === 0) return null;

  const closeViewer = () => { setActiveType(null); setFlashcardIdx(0); setFlashcardFlipped(false); setQuizAnswers({}); };

  const getCount = (key) => {
    if (key === 'flashcards' && Array.isArray(materials[key])) return materials[key].length;
    if (key === 'extra_quiz' && Array.isArray(materials[key])) return materials[key].length;
    return null;
  };

  // Parse slides JSON if stored as string
  const getSlides = () => {
    try {
      const raw = materials.slides_url;
      if (Array.isArray(raw)) return raw;
      if (typeof raw === 'string' && raw.startsWith('[')) return JSON.parse(raw);
      return null;
    } catch { return null; }
  };

  // Parse quiz data
  const getQuiz = () => {
    const raw = materials.extra_quiz;
    if (Array.isArray(raw)) return raw;
    try { return typeof raw === 'string' ? JSON.parse(raw) : null; } catch { return null; }
  };

  return (
    <div data-testid="materials-bar">
      {/* Material Cards Grid */}
      {!activeType && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {available.map(([key, config]) => {
            const Icon = config.icon;
            const count = getCount(key);
            const descriptions = {
              report_content: 'Dersin detaylı mevzuat raporu',
              flashcards: 'Sınava hazırlık için soru-cevap kartları',
              extra_quiz: 'Bilginizi test edin',
              audio_url: 'Dinleyerek öğrenmek için senaryo',
              slides_url: 'Konu başlıklarının özeti',
            };
            return (
              <button key={key} onClick={() => setActiveType(key)} data-testid={`mat-${key}`}
                className={`text-left p-4 rounded-xl bg-gradient-to-br ${config.bg} border hover:scale-[1.02] transition-all`}>
                <Icon className={`w-6 h-6 ${config.color} mb-3`} />
                <div className="text-sm font-bold text-foreground">{config.label}{count ? ` (${count})` : ''}</div>
                <p className="text-[11px] text-muted-foreground mt-1 leading-snug">{descriptions[key] || ''}</p>
              </button>
            );
          })}
        </div>
      )}

      {/* Content Viewer */}
      {activeType && (
        <Card className="border overflow-hidden animate-fade-in" data-testid="material-viewer">
          <CardContent className="p-0">
            {/* Viewer Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/20">
              <div className="flex items-center gap-2">
                <button onClick={closeViewer} className="p-1.5 rounded-lg hover:bg-muted/50 transition-colors mr-1">
                  <ChevronLeft className="w-4 h-4 text-muted-foreground" />
                </button>
                {React.createElement(TYPES[activeType]?.icon || FileText, { className: `w-5 h-5 ${TYPES[activeType]?.color}` })}
                <span className="text-base font-semibold">{TYPES[activeType]?.label}</span>
              </div>
            </div>

            <div className="p-4">
              {/* Report */}
              {activeType === 'report_content' && materials.report_content && (
                <div className="prose prose-sm dark:prose-invert max-h-[450px] overflow-y-auto text-sm leading-relaxed whitespace-pre-wrap">
                  {materials.report_content.replace(/\*\*(.*?)\*\*/g, '$1').replace(/^#{1,3}\s/gm, '')}
                </div>
              )}

              {/* Flashcards */}
              {activeType === 'flashcards' && Array.isArray(materials.flashcards) && materials.flashcards.length > 0 && (
                <div className="space-y-4">
                  <div
                    className="min-h-[180px] rounded-xl border-2 border-dashed border-border flex items-center justify-center p-6 cursor-pointer transition-all hover:border-primary/30 hover:bg-muted/20"
                    onClick={() => setFlashcardFlipped(!flashcardFlipped)}
                    data-testid="flashcard"
                  >
                    <div className="text-center max-w-md">
                      <div className={`text-[10px] uppercase tracking-wider mb-3 font-medium ${flashcardFlipped ? 'text-emerald-400' : 'text-primary'}`}>
                        {flashcardFlipped ? 'Cevap' : 'Soru'}
                      </div>
                      <div className="text-base font-medium leading-relaxed">
                        {flashcardFlipped ? materials.flashcards[flashcardIdx]?.back : materials.flashcards[flashcardIdx]?.front}
                      </div>
                      {flashcardFlipped && materials.flashcards[flashcardIdx]?.ref && (
                        <div className="mt-3 text-[10px] text-primary/50">{materials.flashcards[flashcardIdx].ref}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <Button size="sm" variant="ghost" disabled={flashcardIdx === 0}
                      onClick={() => { setFlashcardIdx(flashcardIdx - 1); setFlashcardFlipped(false); }}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-xs text-muted-foreground">{flashcardIdx + 1} / {materials.flashcards.length}</span>
                    <Button size="sm" variant="ghost" disabled={flashcardIdx >= materials.flashcards.length - 1}
                      onClick={() => { setFlashcardIdx(flashcardIdx + 1); setFlashcardFlipped(false); }}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-[10px] text-center text-muted-foreground/40">Karta tikla, cevabi gor</p>
                </div>
              )}

              {/* Quiz */}
              {activeType === 'extra_quiz' && (() => {
                const quiz = getQuiz();
                if (!quiz || quiz.length === 0) return <p className="text-sm text-muted-foreground">Quiz icerigi yok</p>;
                return (
                  <div className="space-y-4 max-h-[500px] overflow-y-auto">
                    {quiz.map((q, qi) => (
                      <div key={qi} className="p-4 rounded-lg bg-muted/20 border border-border">
                        <p className="text-sm font-medium mb-3">{qi + 1}. {q.question}</p>
                        <div className="space-y-1.5">
                          {(q.options || []).map((opt, oi) => {
                            const answered = quizAnswers[qi] !== undefined;
                            const selected = quizAnswers[qi] === oi;
                            const isCorrect = oi === q.correct;
                            return (
                              <button key={oi}
                                onClick={() => !answered && setQuizAnswers(p => ({ ...p, [qi]: oi }))}
                                className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-all border ${
                                  answered && isCorrect ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' :
                                  answered && selected && !isCorrect ? 'bg-red-500/10 border-red-500/30 text-red-400' :
                                  answered ? 'border-border opacity-50' :
                                  'border-border hover:bg-muted/30 hover:border-primary/20'
                                }`}
                                disabled={answered}
                              >
                                <span className="flex items-center gap-2">
                                  {answered && isCorrect && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
                                  {opt}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                        {quizAnswers[qi] !== undefined && q.explanation && (
                          <p className="mt-2 text-[11px] text-muted-foreground italic">{q.explanation}</p>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Podcast Script */}
              {activeType === 'audio_url' && materials.audio_url && (
                <div className="max-h-[450px] overflow-y-auto">
                  <div className="flex items-center gap-2 mb-3 pb-3 border-b border-border">
                    <Mic className="w-4 h-4 text-red-400" />
                    <span className="text-xs text-muted-foreground">Podcast Senaryosu</span>
                  </div>
                  <div className="text-sm leading-relaxed whitespace-pre-wrap text-foreground/80">
                    {materials.audio_url}
                  </div>
                </div>
              )}

              {/* Slides */}
              {activeType === 'slides_url' && (() => {
                const slides = getSlides();
                if (!slides) {
                  // URL-based slides
                  if (materials.slides_url && materials.slides_url.startsWith('http')) {
                    return <iframe src={materials.slides_url} className="w-full h-[400px] rounded-lg border-0" allowFullScreen />;
                  }
                  return <p className="text-sm text-muted-foreground">Slayt icerigi yok</p>;
                }
                return (
                  <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                    {slides.map((slide, si) => (
                      <div key={si} className="p-4 rounded-lg bg-muted/20 border border-border">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-[10px] text-muted-foreground bg-muted/50 px-2 py-0.5 rounded">{si + 1}</span>
                          <h4 className="text-sm font-semibold">{slide.title}</h4>
                        </div>
                        <ul className="space-y-1 ml-4">
                          {(slide.bullets || []).map((b, bi) => (
                            <li key={bi} className="text-xs text-foreground/70 list-disc">{b}</li>
                          ))}
                        </ul>
                        {/* slide.notes: eğitmene yönelik notlar — öğrenciye gösterilmez */}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
